/*
 * ==================================================================================================================================================
 * @file useRetryManager.ts
 * @description Composable que gestiona el modal de alerta por pérdida de conexión y reintenta acciones fallidas automáticamente.
 * ==================================================================================================================================================
 */

import { ref } from "vue";
import { checkConnection } from "@/services/apiServices/checkConnection"; // 💡 Usa ping real

// 📌 Estado reactivo que indica si debe mostrarse el modal de reintento
const showRetryModal = ref(false);

// 📌 Estado que indica si se está intentando actualmente reintentar la acción
const isRetrying = ref(false);

// 📌 Acción pendiente que se intentará de nuevo cuando el usuario pulse “Reintentar”
const pendingAction = ref<null | (() => Promise<any>)>(null);

/**
 * ⏱️ Espera activa utilizada para mantener el spinner visible al menos X ms.
 * Evita que el modal desaparezca instantáneamente si la red se recupera muy rápido.
 */
function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * 🌐 retryManager: gestor global de acciones pendientes por error de red.
 * Se integra con el interceptor Axios y el modal RetryModal.vue
 */
export const retryManager = {
  // ▶️ Controla la visibilidad del modal global de reintento
  showRetryModal,

  // 🔄 Indica si el sistema está reintentando la acción fallida
  isRetrying,

  /**
   * 🧩 Registra la acción que ha fallado por red para poder reintentarse después
   * @param action - función asíncrona que representa la petición original
   */
  setPendingAction(action: () => Promise<any>) {
    pendingAction.value = action;
    showRetryModal.value = true;
  },

  /**
   * 🔍 Getter para acceder a la acción pendiente desde fuera
   */
  getPendingAction() {
    return pendingAction;
  },

  /**
   * 🔁 Intenta ejecutar la acción previamente guardada
   * Si tiene éxito, se cierra el modal.
   * Si sigue sin red, mantiene el modal abierto y muestra advertencia.
   */
  async retry() {
    if (!pendingAction.value) {
      console.warn("❌ No hay acción pendiente para reintentar");
      return;
    }

    console.log("🔁 Reintentando acción pendiente...");
    isRetrying.value = true; // se muestra spinner

    // 🛑 Antes de reintentar, confirmamos conexión real
    const isConnected = await checkConnection();
    if (!isConnected) {
      console.warn("🔌 Sigue sin conexión, cancelando reintento");
      await delay(1000);
      isRetrying.value = false; // se elimina spinner
      return;
    }

    try {
      await pendingAction.value(); // ✅ Ejecuta la acción original
      await delay(1000); // 💡 Mantiene el spinner visible al menos 1s
      showRetryModal.value = false; // ✅ Oculta modal
      pendingAction.value = null;
    } catch (e) {
      await delay(1000);
      if (!navigator.onLine) {
        console.warn("🔌 Reintento fallido por red");
        // ❌ Red aún caída, mantenemos el modal abierto
      } else {
        console.warn("⚠️ Error inesperado al reintentar, cerrando modal");
        showRetryModal.value = false;
        pendingAction.value = null;
      }
    } finally {
      isRetrying.value = false; // 🔓 Habilita botón de nuevo
    }
  },
};
